<template>
  <div :class="{ 'pt-20 md:pt-[115px]': simpleLayout }">
    <HeroView v-if="!simpleLayout" :align :badge :banner :subtitle :title>
      <template #breadcrumb>
        <div :class="{ 'mb-44': isWebview || !displayBreadcrumb }">
          <Breadcrumb
            v-if="!isWebview && !simpleLayout && displayBreadcrumb"
            :current-page-title="titleBreadcrumb"
            :intermediary-path
            @breadcrumb-clicked="
              (breadcrumbTitle) =>
                handleClickEvent({ title: breadcrumbTitle, type: 'Breadcrumb' })
            "
          />
        </div>
      </template>
    </HeroView>

    <TheMainContent aria-labelledby="cms-heading-title">
      <Container
        v-for="(block, index) in blocks"
        :key="block.id"
        :width="getBlockWidth(block.type)"
      >
        <BlockRenderer
          :id="block.id"
          :block-position="index + 1"
          :data-test="block.id"
          :hidden-devices="block.hiddenDevices"
          :props="block.props"
          :type="block.type"
        />
      </Container>
    </TheMainContent>

    <RevContainer v-if="textSeo && hasRichText" class="pb-72">
      <RichText
        :data-selector="`${pageType}-content`"
        light-text
        :rich-text="textSeo"
      />
    </RevContainer>
  </div>
</template>

<script lang="ts" setup>
import { useRoute } from '#imports'
import { computed, onMounted } from 'vue'

import type { Banner } from '@backmarket/http-api/src/api-specs-content/models/banner'
import type { BasePageContent } from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import type { BlockType } from '@backmarket/http-api/src/api-specs-content/models/block'
import Container from '@backmarket/nuxt-layer-cms/Container.vue'
import RichText from '@backmarket/nuxt-layer-cms/RichText.vue'
import { getBlockWidth } from '@backmarket/nuxt-layer-cms/utils/blockWidth'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevContainer } from '@ds/components/Container'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'

import { buildTrackingZone } from '../helpers/tracking/tracking'
import BlockRenderer from '../shared-components/BlockRenderer/BlockRenderer.vue'
import Breadcrumb from '../shared-components/Breadcrumb/Breadcrumb.vue'
import { getIntermediaryPath } from '../shared-components/Breadcrumb/helpers/get-intermediary-path'
import HeroView from '../shared-components/HeroView/HeroView.vue'

const { trackClick, trackLandingPage } = useTracking()
const route = useRoute()

const props = withDefaults(
  defineProps<
    BasePageContent & { simpleLayout?: boolean; isWebview?: boolean }
  >(),
  {
    parentPageInfos: undefined,
    simpleLayout: false,
    displayBreadcrumb: true,
    isWebview: false,
  },
)

onMounted(() => {
  if (route.name) {
    trackLandingPage({
      landingType: route.name.toString(),
      landingName: buildTrackingZone([
        route.params.pageName,
        route.params.pageCategory,
      ]),
    })
  }
})

const banner = computed<Banner | null>(() => {
  if (isEmpty(props.banner)) return null

  return props.banner as Banner
})

const intermediaryPath = computed(() => {
  const { parentPageInfos, pageType } = props

  return getIntermediaryPath({
    parentPageInfos,
    pageType,
  })
})

function handleClickEvent({
  title,
  type,
}: {
  title: string
  type: 'Breadcrumb' | BlockType
}) {
  if (route.name) {
    const { pageName, pageCategory } = route.params

    trackClick({
      zone: buildTrackingZone([route.name.toString(), pageName, pageCategory]),
      name: title,
      value: type,
    })
  }
}

const hasRichText = computed(() => !isEmpty(props.textSeo))
</script>
